import AlertBox from "../_common/components/AlertBox/AlertBox"
import MatriculacionSkeleton from "./matriculacion-skeleton"
import ButtonSpinner from "../_common/components/buttons/buttonSpinner/buttonSpinner"
import { Checkbox } from "../_common/forms/components/Checkbox/Checkbox"
import { h, Fragment } from "preact"
import { t } from '../_i18n'
import { useEffect } from 'preact/hooks'

import './matriculacion-modal.scss'

export default function MatriculacionModal({
    cartData,
    cartDataAlreadyLoaded,
    isPremiumUser,
    loadingContinue,
    loadingSkeleton,
    onCalculateDiscount,
    onCloseModal,
    onContinue,
    premiumSelected,
    selectedProducts,
    setLoadingContinue,
    setPremiumSelected,
    setSelectedProducts,
    setToValidate,
    title,
    toValidate
}) {
    /** Variables **/
    const premiumProductId = 970

    /** Lifecycles **/
    useEffect(() => {
        if (!cartDataAlreadyLoaded) onCalculateDiscount([], true)
    }, [])

    useEffect(() => {
        if (!toValidate) return;

        if(selectedProducts.length === 0) {
            setLoadingContinue(false);
            setToValidate(false);
            return;
        }

        const timeout = setTimeout(() => {
            if (selectedProducts.length === 0 && !premiumSelected) {
                cartData = {
                    ...cartData,
                    total: 0,
                    cartRules: []
                }
            }
            handleCalculateDiscount();
        }, 2000);

        return () => clearTimeout(timeout);
    }, [selectedProducts, premiumSelected]);

    /** Methods **/
    const renderLoading = () => {
        if (loadingSkeleton) {
            return <MatriculacionSkeleton />
        }
        return null
    }

    const getSemesterText = (semester) => {
        const names = [
            t('PRIMER'),
            t('SEGUNDO'),
            t('TERCER'),
            t('CUARTO')
        ]

        const name = names[semester - 1] || ''
        return `${name} ${t('SEMESTRE')}`
    }

    const getSubtotal = () => {
        let subtotal = 0

        cartData?.semesters?.forEach(({ products }) => {
            products.forEach((product) => {
                if (selectedProducts?.includes(product.id_product)) {
                    subtotal += product.price
                }
            })
        })

        if (premiumSelected) subtotal += 60

        return subtotal.toFixed(2)
    }

    const handleCheckboxChange = (productId) => {
        if(loadingContinue) return;

        setToValidate(true)

        let updatedProducts = [...selectedProducts]

        if (updatedProducts.includes(productId)) {
            updatedProducts = updatedProducts.filter(id => id !== productId)
        } else {
            updatedProducts.push(productId)
        }

        setSelectedProducts(updatedProducts)
    }

    const togglePremium = () => {
        if(loadingContinue) return;
        
        setToValidate(true);

        const newPremiumState = !premiumSelected;
        setPremiumSelected(newPremiumState);

        setSelectedProducts(prevProducts => {
            if (newPremiumState) {
                return [...prevProducts, premiumProductId];
            } else {
                return prevProducts.filter(id => {
                    return id !== premiumProductId
                });
            }
        });
    };

    const handleCalculateDiscount = async () => {
        setToValidate(false)
        await onCalculateDiscount(selectedProducts)
    }

    const handleContinue = async () => {
        if (loadingContinue) return;
        setToValidate(false)
        await onContinue()
    }

    const checkIfPremiumShipping = (rule) => {
        const premiumShipping = rule.reduction_percent === '0.000000' && rule.name?.includes('PREMIUM');
        if (premiumShipping) {
            return { ...rule, value_transporte: t("Envío gratuito") }
        } else {
            return rule
        }
    }

    const getRuleName = (realDescription, name) => {
        if (!realDescription) return name;

        const isMatch =
            realDescription === 'Descuento matriculación 2526' ||
            realDescription.startsWith('PK-S') ||
            realDescription.startsWith('PK-A');

        if (isMatch) {
            return t('Descuento matriculación');
        }

        return name;
    };


    /** Render **/
    return (
        <div className="matriculacion-modal">
            {renderLoading() ?? (
                <div>
                    {/* Header */}
                    <div className="d-flex flex-space-between align-center">
                        <div className="font-14">
                            <strong>{title}</strong>
                        </div>
                        <a onClick={onCloseModal} className="modal-close-jobs modal-link">
                            <i className="fal fa-times font-20 " />
                        </a>
                    </div>

                    {/* Title */}
                    <h2 className="font-26 m-bottom">
                        <strong>{t('Configura tu próximo semestre')}</strong>
                    </h2>

                    {/* Description */}
                    <div className="m-bottom">
                        <AlertBox type='info' className='m-bottom'>
                            {t('Hemos preparado una propuesta de matrícula para tu próximo semestre, basada en lo que estás cursando ahora.')}
                        </AlertBox>
                    </div>

                    {/* Body */}
                    <Fragment>
                        <hr className="m-bottom matriculacion-modal__divider" />
                        {cartData?.semesters?.map(({ semester, products }) => (
                            <div key={semester}>
                                <p className="no-margin m-bottom txt-disabled font-16">
                                    {getSemesterText(semester)}
                                </p>

                                {products
                                    .filter((product) => product.price > 0)
                                    .map((product) => (
                                        <div
                                            key={product.id_product}
                                            className="d-flex flex-space-between align-center product-row m-bottom"
                                        >
                                            <Checkbox
                                                className="matriculacion-modal__checkbox"
                                                label={product.product_name || product.name}
                                                onChange={() => handleCheckboxChange(product.id_product)}
                                                value={selectedProducts.includes(product.id_product)}
                                                labelIsClicked={loadingContinue ? false : true}
                                                disabled={loadingContinue}
                                            />

                                            <div className={`t-right ${loadingContinue && 'txt-disabled'}`}>
                                                <strong>{product.price.toFixed(2)}€</strong>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        ))}
                    </Fragment>

                    <hr className="m-bottom matriculacion-modal__divider" />

                    {/* Premium */}
                    {!isPremiumUser && (
                        <Fragment>
                            <div className="d-flex flex-space-between align-center">
                                <Checkbox
                                    className="matriculacion-modal__checkbox"
                                    label={t('Premium (libros en formato papel incluidos)')}
                                    onChange={togglePremium}
                                    value={premiumSelected}
                                    labelIsClicked={loadingContinue ? false : true}
                                    disabled={loadingContinue}
                                />
                                <strong className={`${loadingContinue && 'txt-disabled'}`}>60.00€</strong>
                            </div>
                            <hr className="m-bottom matriculacion-modal__divider matriculacion-modal__divider--strong" />
                        </Fragment>
                    )}

                    {/* Totals */}
                    <div className="d-flex flex-column flex-bottom m-bottom gap-1">
                        <span className="m-bottom">{t('Subtotal:')} {getSubtotal()}€</span>
                        {(selectedProducts.length || premiumSelected) ?
                            (loadingContinue || toValidate)
                            ? <div class="loading loading__line loading__line--w60 m-bottom" />
                            : cartData?.cartRules && <div className="m-bottom d-flex flex-column flex-right gap-1">
                                {cartData?.cartRules
                                    ?.map(rule => checkIfPremiumShipping(rule))
                                    ?.map(rule => (
                                        <div key={rule?.id} class="d-flex gap-1 flex-right">
                                            <span>{getRuleName(rule?.real_description, rule?.name)}: </span>
                                            {!rule?.value_transporte ?
                                                <div className="matriculacion-modal__badge matriculacion-modal__badge--yellow font-14">
                                                    <strong>-{Number(rule?.reduction_percent)?.toFixed(0)}% dto</strong>
                                                </div> :
                                                <div className="matriculacion-modal__badge matriculacion-modal__badge--yellow font-14">
                                                    <strong>{rule?.value_transporte}</strong>
                                                </div>
                                            }
                                            {!rule?.value_transporte &&
                                                <span>-{rule?.value_real?.toFixed(2)}€</span>}
                                        </div>
                                    ))}
                            </div> : null
                        }
                        <div className="d-flex m-bottom-half font-24 gap-2 flex-center-y full-width flex-right">
                            <span>{t('Total matrícula:')}</span>
                            {(loadingContinue || toValidate)
                                ? <div class="loading loading__line loading__line--w20 loading__line--h40" />
                                : <strong>{(selectedProducts.length || premiumSelected)
                                    ? cartData?.total?.toFixed(2)
                                    : "0.00"}€</strong>
                            }
                        </div>
                    </div>

                    {/* Continue Button */}
                    <div className="d-flex flex-right">
                        <ButtonSpinner
                            onClick={handleContinue}
                            showSpinner={loadingContinue || toValidate}
                            btnType="primary"
                            disabled={selectedProducts.length === 0 && !premiumSelected}
                        >
                            {t('Continuar')}
                        </ButtonSpinner>
                    </div>
                </div>
            )}
        </div>
    )
}
